import InfiniteScroll from 'react-infinite-scroll-component';
import Big from 'big.js';
import classNames from 'classnames';
import { get } from 'lodash';
import { useId, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { DistributionMore } from '@/modules/issuer-dashboard/components/distribution-dashboard/DistributionMore.tsx';
import { DistributionStage } from '@/modules/issuer-dashboard/components/distribution-dashboard/DistributionStage.tsx';
import { IssuerActivitiesOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerActivitiesOperationInfoModal.tsx';
import {
  InfoItem,
  IssuerTransactionsOperationInfoModal,
} from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import { useIssuerActivitiesListQuery } from '@/modules/issuer-dashboard/queries/activities.tsx';
import { useIssuerDividendEventByIdQuery } from '@/modules/issuer-dashboard/queries/dividends.tsx';
import { useTransactionsListQuery } from '@/modules/issuer-dashboard/queries/transactions.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { Badge, BaseModalProps, ModalContentWrapper, Spinner } from 'ui';
import {
  fixNameEnum,
  goodFormatNumber,
  goodFormatUSDCurrency,
  goodFormatUSDCurrencyLowPrecision,
} from 'ui/utils';
import {
  ActivitiesPaginateResponse,
  ActivitiesType,
  DividendEventExtendResponse,
  DividendStatus,
  TransactionPaginateResponse,
  TransactionType,
} from 'api';

interface DividendOperationInfoModalProps extends BaseModalProps {
  id: number;
  registerId: string;
  registerCountry: string;
  isDeleted?: boolean;
}

export const DividendOperationInfoModal = ({
  id,
  open,
  onClose,
  registerId,
  registerCountry,
  isDeleted = false,
}: DividendOperationInfoModalProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const scrollableId = useId();
  const { data, isLoading: isOperationLoading } = useIssuerDividendEventByIdQuery({
    id,
    is_deleted: isDeleted,
  });

  const operationData = get(data, 'data') as DividendEventExtendResponse;

  const completionDate = get(operationData, 'date');

  return (
    <ModalContentWrapper
      variant="right"
      title={
        <div className="flex gap-4 items-center">
          <span>{`${getRegisterTypeStrings('dividend', { capitalize: true })} info`}</span>
          {isDeleted && (
            <Badge className="bg-red-400 text-white text-xs font-[500] font-[Inter]">
              Archived
            </Badge>
          )}
        </div>
      }
      open={open}
      onClose={onClose}
      classes={{ children: 'p-0 pb-0' }}
      scrollableTarget={scrollableId}
    >
      {isOperationLoading && (
        <div className="w-full h-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isOperationLoading && operationData && (
        <>
          <div className="flex flex-col justify-between gap-4 h-full">
            <div
              className={classNames('space-y-3 px-3 sm:px-6 pt-3 sm:pt-6  overflow-x-auto', {
                'max-h-[calc(100dvh-300px)]': operationData.status === DividendStatus.Pending,
                'max-h-[calc(100dvh-240px)]': operationData.status === DividendStatus.Completed,
              })}
            >
              <InfoItem title="ID">
                <div>#{operationData.id}</div>
              </InfoItem>
              <InfoItem title="Name">
                <div>{operationData.name}</div>
              </InfoItem>
              <InfoItem title="Status">
                <ActivityStatusBadge status={operationData.status} />
              </InfoItem>
              {operationData.status !== DividendStatus.Completed && (
                <InfoItem title="Stage">
                  <DistributionStage item={operationData} />
                </InfoItem>
              )}
              <InfoItem title="Class">
                <Link
                  state={{ from: pathname + '?' + params.toString() }}
                  to={{
                    pathname: issuerDashboardPaths.dynamic.classById(
                      (operationData.register_class?.id || '0').toString(),
                    ),
                    search: params.toString(),
                  }}
                  className="text-blue-700 hover:underline"
                >
                  {operationData.register_class.class_name}
                </Link>
              </InfoItem>

              <InfoItem title="Type">
                <div>{fixNameEnum(operationData.type)}</div>
              </InfoItem>
              {operationData.total_quantity !== undefined && (
                <InfoItem
                  title={`${getRegisterTypeStrings('dividend', { capitalize: true })} units/shares`}
                >
                  <div>{goodFormatNumber(operationData.total_quantity)}</div>
                </InfoItem>
              )}
              <InfoItem title="$ per share/unit">
                <div>{goodFormatUSDCurrency(operationData.amount_per_share)}</div>
              </InfoItem>
              {operationData.total_amount !== undefined && (
                <InfoItem
                  title={`${getRegisterTypeStrings('dividend', { capitalize: true })} amount`}
                >
                  <div>{goodFormatUSDCurrencyLowPrecision(operationData.total_amount)}</div>
                </InfoItem>
              )}
              {/* <InfoItem title="Requested date"> */}
              {/*   <div>{dateToPrint(operationData.requested_at_date, DateVariant.Information)}</div> */}
              {/* </InfoItem> */}
              <InfoItem title="Completion date">
                <div>
                  {operationData?.status === DividendStatus.Completed
                    ? dateToPrint(completionDate, DateVariant.Information)
                    : '-'}
                </div>
              </InfoItem>
              <InfoItem title="Memo" align="start">
                <div>{operationData.memo || '-'}</div>
              </InfoItem>

              {/* transactions */}
              <Reinvestments
                dividendId={id}
                registerId={registerId}
                registerCountry={registerCountry}
                scrollableTarget={scrollableId}
                isDeleted={isDeleted}
              />
              <Payments
                dividendId={id}
                registerId={registerId}
                registerCountry={registerCountry}
                scrollableTarget={scrollableId}
                isDeleted={isDeleted}
              />
            </div>
            <div className="flex flex-col gap-4 w-full px-3 sm:px-6 pb-3 sm:pb-6">
              <DistributionMore
                item={operationData}
                registerId={registerId}
                registerCountry={registerCountry}
                inModal={true}
                isDeleted={isDeleted}
              />
            </div>
          </div>
          {/* modals */}
        </>
      )}
    </ModalContentWrapper>
  );
};

interface ReinvestmentsProps {
  dividendId: number;
  registerId: string;
  registerCountry: string;
  scrollableTarget: string;
  isDeleted?: boolean;
}

const Reinvestments = ({
  dividendId,
  registerId,
  registerCountry,
  scrollableTarget,
  isDeleted = false,
}: ReinvestmentsProps) => {
  const {
    data: issuerActivitiesList,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useTransactionsListQuery(
    {
      page: 1,
      page_size: 30,
      type: TransactionType.Drp,
      dividend_event_id: dividendId,
      register_id: Number(registerId),
      is_deleted: isDeleted,
    },
    false,
    Number(registerId),
  );

  const transactions = useMemo(
    () => (issuerActivitiesList?.pages || []).flatMap(({ data }) => data),
    [issuerActivitiesList?.pages],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32 w-full">
        <Spinner size="lg" variant="primary" />
      </div>
    );
  }

  if (transactions?.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className="text-[16px] leading-[22px] font-[500] pt-6 pb-4 border-b border-b-gray-200">
        Reinvestments
      </div>

      <InfiniteScroll
        scrollableTarget={scrollableTarget}
        next={fetchNextPage}
        dataLength={transactions?.length ?? 0}
        hasMore={hasNextPage ?? false}
        loader={
          <div className="flex justify-center items-center h-16 w-full">
            <Spinner size="md" variant="primary" />
          </div>
        }
        style={{ overflow: 'hidden!important' }}
      >
        {transactions?.map((item, i) => (
          <ReinvestmentItem
            item={item}
            key={`transaction_${i}`}
            registerId={registerId}
            registerCountry={registerCountry}
            isDeleted={isDeleted}
          />
        ))}
      </InfiniteScroll>
    </>
  );
};

export interface ReinvestmentItemProps {
  item: TransactionPaginateResponse;
  registerId: string;
  registerCountry: string;
  isDeleted?: boolean;
}

const ReinvestmentItem = ({
  item,
  registerId,
  registerCountry,
  isDeleted = false,
}: ReinvestmentItemProps) => {
  const [openFull, setOpenFull] = useState(false);
  const { status, quantity, price_per_share, id, shareholder_entity } = item;

  const calculateTotal = useMemo<string>(() => {
    const calculatedValue = new Big(quantity || 0).mul(price_per_share || 0).valueOf();
    return goodFormatUSDCurrencyLowPrecision(+calculatedValue);
  }, [price_per_share, quantity]);

  return (
    <>
      <div
        className="flex w-full gap-4 justify-between py-2 border-b border-b-gray-200 hover:bg-gray-50 cursor-pointer"
        onClick={() => setOpenFull(true)}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">#{id}</span>
            <ActivityStatusBadge status={status || ''} />
          </div>
          <div className="text-xs text-gray-700">{shareholder_entity?.entity_name}</div>
        </div>

        <div className="flex flex-col gap-1 text-right">
          <div className="whitespace-nowrap text-nowrap text-sm leading-[22px] text-gray-700 flex items-center gap-2">
            <span>
              <span className="text-gray-500 text-xs">Quantity:</span>{' '}
              {goodFormatNumber(quantity ?? 0)}
            </span>
            <span className="text-gray-500 text-xs">/</span>
            <span>
              <span className="text-gray-500 text-xs">Price:</span>{' '}
              {goodFormatUSDCurrency(price_per_share ?? 0)}
            </span>
          </div>

          <div className="whitespace-nowrap text-nowrap text-sm leading-[22px] text-gray-700">
            <span>
              <span className="text-gray-500 text-xs">Total:</span> {calculateTotal}
            </span>
          </div>
        </div>
      </div>
      {openFull && (
        <IssuerTransactionsOperationInfoModal
          open={openFull}
          onClose={() => setOpenFull(false)}
          {...{
            id: item.id,
            title: 'Reinvestment info',
            registerId,
            registerCountry,
            isDeleted,
          }}
        />
      )}
    </>
  );
};

interface PaymentsProps {
  dividendId: number;
  registerId: string;
  registerCountry: string;
  scrollableTarget: string;
  isDeleted?: boolean;
}

const Payments = ({
  dividendId,
  registerId,
  registerCountry,
  scrollableTarget,
  isDeleted = false,
}: PaymentsProps) => {
  const {
    data: issuerActivitiesList,
    fetchNextPage,
    hasNextPage,
    // isLoading,
  } = useIssuerActivitiesListQuery(
    {
      page: 1,
      page_size: 30,
      is_dividend_activities: true,
      type: ActivitiesType.FundToInvestor,
      dividend_id: dividendId,
      is_deleted: isDeleted,
    },
    false,
    Number(registerId),
  );

  const transactions = useMemo(
    () => (issuerActivitiesList?.pages || []).flatMap(({ data }) => data),
    [issuerActivitiesList?.pages],
  );

  if (transactions?.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className="text-[16px] leading-[22px] font-[500] pt-6 pb-4 border-b border-b-gray-200">
        Payments
      </div>

      <InfiniteScroll
        scrollableTarget={scrollableTarget}
        next={fetchNextPage}
        dataLength={transactions?.length ?? 0}
        hasMore={hasNextPage ?? false}
        loader={
          <div className="flex justify-center items-center h-16 w-full">
            <Spinner size="md" variant="primary" />
          </div>
        }
        style={{ overflow: 'hidden!important' }}
      >
        {transactions?.map((item, i) => (
          <PaymentItem
            item={item}
            key={`transaction_${i}`}
            registerId={registerId}
            registerCountry={registerCountry}
            isDeleted={isDeleted}
          />
        ))}
      </InfiniteScroll>
    </>
  );
};

export interface PaymentItemProps {
  item: ActivitiesPaginateResponse;
  registerId: string;
  registerCountry: string;
  isDeleted?: boolean;
}

const PaymentItem = ({
  item,
  registerId,
  registerCountry,
  isDeleted = false,
}: PaymentItemProps) => {
  const [openFull, setOpenFull] = useState(false);
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { status, amount, completion_date, requested_at_date, id, from_entity_name } = item;

  return (
    <>
      <div
        className="flex w-full gap-4 justify-between py-2 border-b border-b-gray-200 hover:bg-gray-50 cursor-pointer"
        onClick={() => setOpenFull(true)}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-sm leading-[22px] text-[#0369A1] font-[500] min-w-fit">
              #{id}
            </span>
            <ActivityStatusBadge status={status || ''} className="min-w-fit" />
          </div>

          <div className="text-gray-500 text-sm w-[118px]">
            {dateToPrint(completion_date || requested_at_date, DateVariant.Table, true)}
          </div>
        </div>

        <div className="flex flex-col gap-1 items-end text-right">
          <div className="whitespace-nowrap text-nowrap text-sm leading-[22px] text-gray-700 flex items-center gap-2">
            <span>
              <span className="text-gray-500 text-xs">{`${getRegisterTypeStrings('dividend', {
                capitalize: true,
              })} amount:`}</span>{' '}
              {goodFormatUSDCurrencyLowPrecision(amount)}
            </span>
          </div>

          <div className="text-xs text-gray-700">
            <span>{from_entity_name}</span>
          </div>
        </div>
      </div>
      {openFull && (
        <IssuerActivitiesOperationInfoModal
          open={openFull}
          onClose={() => setOpenFull(false)}
          {...{
            registerId,
            registerCountry,
            paymentId: id,
            isDeleted,
          }}
        />
      )}
    </>
  );
};
